import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUploader = ({ category = 'products', onUploadSuccess, currentImage }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || '');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const imageUrl = response.data.url;
      setPreview(`${BACKEND_URL}${imageUrl}`);
      onUploadSuccess(imageUrl);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
      setPreview(currentImage || '');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm text-gray-400">Image</label>
      
      {/* Preview */}
      {preview && (
        <div className="relative w-full h-48 bg-[#2A2720] rounded overflow-hidden">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-full object-cover"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center text-white"
          >
            <span className="bg-[#C9A961] px-4 py-2 rounded">Change Image</span>
          </button>
        </div>
      )}

      {/* Upload Button */}
      {!preview && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full px-4 py-12 border-2 border-dashed border-[#C9A961]/30 hover:border-[#C9A961] rounded transition-colors flex flex-col items-center justify-center text-gray-400 hover:text-[#C9A961]"
        >
          <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          {uploading ? 'Uploading...' : 'Click to upload image'}
          <span className="text-xs mt-2">PNG, JPG, WebP up to 5MB</span>
        </button>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Manual URL Input (fallback) */}
      <div className="pt-2">
        <input
          type="url"
          value={preview}
          onChange={(e) => {
            setPreview(e.target.value);
            onUploadSuccess(e.target.value);
          }}
          placeholder="Or paste image URL"
          className="w-full px-4 py-2 bg-[#1A1915] border border-[#C9A961]/30 text-white text-sm placeholder-gray-500 focus:border-[#C9A961] focus:outline-none"
        />
      </div>
    </div>
  );
};

export default ImageUploader;
